import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Home, Shield, CheckCircle2, Sun, Moon, Mail, Trash2 } from 'lucide-react';
import DiscountBanner from '../components/DiscountBanner';
import ReviewBanner from '../components/ReviewBanner';

const HomeGuardiansPage = () => {
  return (
    <div className="home-guardians-page">
      {/* Discount Banner - Top */}
      <DiscountBanner size="large" />
      
      {/* Review Banner */}
      <ReviewBanner />
      
      {/* Hero Section */}
      <section className="page-hero">
        <div className="container">
          <h1 className="page-title">Home Guardians Service</h1>
          <p className="page-subtitle">Keep your home secure and lived-in while you're away</p>
        </div>
      </section>

      {/* Introduction */}
      <section className="guardians-intro-section">
        <div className="container">
          <div className="intro-content-grid">
            <div className="intro-text">
              <h2 className="section-title">More Than Just Cat Sitting</h2>
              <p className="intro-paragraph">
                When you're away from home, it's not just your cats you worry about. An empty house can be a target for opportunistic thieves, and returning to an obviously unoccupied home can feel unsettling.
              </p>
              <p className="intro-paragraph">
                That's where our Home Guardians service comes in. While we're caring for your cats, we also ensure your home looks lived-in and secure, giving you complete peace of mind throughout your travels.
              </p>
              <p className="intro-paragraph">
                This service is included as standard with all our cat sitting visits at no extra charge.
              </p>
            </div>

            <div className="intro-image">
              <img src="https://images.unsplash.com/photo-1677126048815-260f5a18ba24" alt="Secure home with cat" />
            </div>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="guardians-services-section">
        <div className="container">
          <h2 className="section-title">What's Included in Home Guardians</h2>
          <p className="section-intro">We take care of the little details that make your home look occupied</p>
          
          <div className="guardians-services-grid">
            <Card className="guardian-service-card">
              <CardContent className="guardian-service-content">
                <div className="guardian-icon-wrapper">
                  <Sun className="guardian-icon" />
                </div>
                <h3>Open & Close Curtains</h3>
                <p>We open curtains in the morning and close them in the evening, creating the natural rhythm of an occupied home.</p>
              </CardContent>
            </Card>

            <Card className="guardian-service-card">
              <CardContent className="guardian-service-content">
                <div className="guardian-icon-wrapper">
                  <Moon className="guardian-icon" />
                </div>
                <h3>Turn Lights On & Off</h3>
                <p>Strategic lighting throughout the day and evening makes your home look lived-in and deters potential intruders.</p>
              </CardContent>
            </Card>

            <Card className="guardian-service-card">
              <CardContent className="guardian-service-content">
                <div className="guardian-icon-wrapper">
                  <Mail className="guardian-icon" />
                </div>
                <h3>Take In Post & Parcels</h3>
                <p>We collect your mail and packages, preventing that tell-tale pile-up that signals an empty house.</p>
              </CardContent>
            </Card>

            <Card className="guardian-service-card">
              <CardContent className="guardian-service-content">
                <div className="guardian-icon-wrapper">
                  <Trash2 className="guardian-icon" />
                </div>
                <h3>Put Bins Out</h3>
                <p>We'll put your bins out on collection day and bring them back in, maintaining your normal household routine.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="guardians-benefits-section">
        <div className="container">
          <h2 className="section-title">The Peace of Mind Benefits</h2>
          
          <div className="benefits-list-grid">
            <div className="benefit-item-large">
              <Shield className="benefit-large-icon" />
              <h3>Enhanced Security</h3>
              <p>An occupied-looking home is a major deterrent to burglars. Our regular visits and home care activities signal that someone is home.</p>
            </div>

            <div className="benefit-item-large">
              <Home className="benefit-large-icon" />
              <h3>Return to a Welcoming Home</h3>
              <p>Come back to a house that's been cared for, with no overflowing mailbox or obvious signs of absence.</p>
            </div>

            <div className="benefit-item-large">
              <CheckCircle2 className="benefit-large-icon" />
              <h3>Included in Your Cat Care</h3>
              <p>There's no extra charge for our Home Guardians service — it's all part of caring for you and your cats.</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works-section">
        <div className="container">
          <h2 className="section-title">How Home Guardians Works</h2>
          
          <div className="steps-grid">
            <div className="step-card">
              <div className="step-number">1</div>
              <h3>Meet & Greet</h3>
              <p>During our free meet and greet, we'll discuss your specific home care needs and preferences.</p>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <h3>Create Your Plan</h3>
              <p>We'll note which lights you'd like turned on/off, curtain preferences, bin day schedules, and where to place post.</p>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <h3>Daily Care</h3>
              <p>During each cat sitting visit, we complete all your home guardian tasks alongside caring for your cats.</p>
            </div>

            <div className="step-card">
              <div className="step-number">4</div>
              <h3>Stay Updated</h3>
              <p>You'll receive daily updates confirming both your cats' wellbeing and that your home is secure.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Tasks */}
      <section className="additional-tasks-section">
        <div className="container">
          <Card className="additional-tasks-card">
            <CardContent className="additional-tasks-content">
              <h2>Need Something Else?</h2>
              <p>Have other home care needs while you're away? We're happy to discuss additional tasks such as:</p>
              <ul className="additional-tasks-list">
                <li>Watering plants</li>
                <li>Adjusting heating or air conditioning</li>
                <li>Checking specific areas of your home</li>
                <li>Any other reasonable requests</li>
              </ul>
              <p className="additional-note">Just let us know during your meet and greet, and we'll do our best to help.</p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CTA Section */}
      <section className="guardians-cta-section">
        <div className="container">
          <div className="cta-box">
            <h2>Ready for Complete Peace of Mind?</h2>
            <p>Book your cat sitting service with included Home Guardians support today.</p>
            <Link to="/contact">
              <Button size="lg" className="cta-button-large">
                Book Your Free Meet & Greet
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomeGuardiansPage;
